import * as v from 'valibot';

const AlchemyTypeSchema = v.union([
  v.literal('Fire'), v.literal('Water'), v.literal('Earth'),
  v.literal('Air'), v.literal('Life'), v.literal('Shadow'),
]);

const BodyGeneSchema = v.union([
  v.literal('round'), v.literal('tall'), v.literal('ghost'),
  v.literal('blob'), v.literal('fish'),
]);

const EggPatternSchema = v.union([
  v.literal('plain'), v.literal('spots'), v.literal('patches'),
  v.literal('stripes'), v.literal('dots'), v.literal('checks'),
  v.literal('stars'), v.literal('dark'),
]);

const EarGeneSchema = v.union([
  v.literal('round'), v.literal('pointy'), v.literal('floppy'), v.literal('antenna'),
]);

const HornGeneSchema = v.union([
  v.literal('none'), v.literal('nub'), v.literal('twin'), v.literal('crown'),
]);

const TailGeneSchema = v.union([
  v.literal('none'), v.literal('stub'), v.literal('curl'), v.literal('fin'),
]);

const MarkingGeneSchema = v.union([
  v.literal('none'), v.literal('cheeks'), v.literal('freckles'),
  v.literal('stripes'), v.literal('mask'),
]);

const StageSchema = v.union([
  v.literal('egg'), v.literal('baby'), v.literal('teen'),
  v.literal('adult'), v.literal('elder'), v.literal('dead'),
]);

const CauseOfDeathSchema = v.union([
  v.literal('neglect'), v.literal('old-age'), v.literal('expedition'),
]);

const DeathCauseSchema = v.union([
  v.literal('neglect'), v.literal('old-age'), v.literal('expedition'), v.literal('alive'),
]);

const GeneticModifierSchema = v.object({ id: v.string() });

const EvolutionTraitSchema = v.object({
  id: v.string(),
  rarity: v.union([v.literal('common'), v.literal('rare'), v.literal('epic')]),
  inheritedFrom: v.nullable(v.string()),
});

const CareProfileSchema = v.object({
  healthyMeals: v.optional(v.pipe(v.number(), v.integer(), v.minValue(0)), 0),
  snacks: v.optional(v.pipe(v.number(), v.integer(), v.minValue(0)), 0),
  plays: v.optional(v.pipe(v.number(), v.integer(), v.minValue(0)), 0),
  cleanups: v.optional(v.pipe(v.number(), v.integer(), v.minValue(0)), 0),
  lateCleanups: v.optional(v.pipe(v.number(), v.integer(), v.minValue(0)), 0),
  heals: v.optional(v.pipe(v.number(), v.integer(), v.minValue(0)), 0),
  sickTimeMs: v.optional(v.pipe(v.number(), v.minValue(0)), 0),
  correctDisciplines: v.optional(v.pipe(v.number(), v.integer(), v.minValue(0)), 0),
  wrongDisciplines: v.optional(v.pipe(v.number(), v.integer(), v.minValue(0)), 0),
  tantrumsResolved: v.optional(v.pipe(v.number(), v.integer(), v.minValue(0)), 0),
  refusals: v.optional(v.pipe(v.number(), v.integer(), v.minValue(0)), 0),
  heartyMeals: v.optional(v.pipe(v.number(), v.integer(), v.minValue(0)), 0),
  activePlays: v.optional(v.pipe(v.number(), v.integer(), v.minValue(0)), 0),
  comfortPlays: v.optional(v.pipe(v.number(), v.integer(), v.minValue(0)), 0),
  focusPlays: v.optional(v.pipe(v.number(), v.integer(), v.minValue(0)), 0),
  lastToyId: v.optional(v.nullable(v.union([
    v.literal('ball'), v.literal('rope'), v.literal('plush'), v.literal('puzzle'),
  ])), null),
  consecutiveToyUses: v.optional(v.pipe(v.number(), v.integer(), v.minValue(0)), 0),
});

const GenomeSchema = v.object({
  body: BodyGeneSchema,
  eyes: v.literal('basic'),
  mouth: v.literal('basic'),
  ears: v.optional(EarGeneSchema, 'round'),
  horns: v.optional(HornGeneSchema, 'none'),
  tail: v.optional(TailGeneSchema, 'none'),
  markings: v.optional(MarkingGeneSchema, 'none'),
  paletteHue: v.pipe(v.number(), v.integer(), v.minValue(0), v.maxValue(359)),
  bodyPredisposition: v.optional(
    v.pipe(v.number(), v.integer(), v.minValue(0), v.maxValue(100)),
    50,
  ),
  eggPattern: EggPatternSchema,
  primaryTypes: v.pipe(v.array(AlchemyTypeSchema), v.minLength(1), v.maxLength(3)),
  modifiers: v.array(GeneticModifierSchema),
  inheritedTraits: v.array(EvolutionTraitSchema),
  recessiveGenes: v.optional(v.object({
    body: v.optional(BodyGeneSchema),
    ears: v.optional(EarGeneSchema),
    horns: v.optional(HornGeneSchema),
    tail: v.optional(TailGeneSchema),
    markings: v.optional(MarkingGeneSchema),
    eggPattern: v.optional(EggPatternSchema),
  })),
});

const CreatureStateSchema = v.object({
  hunger: v.pipe(v.number(), v.minValue(0), v.maxValue(100)),
  happiness: v.pipe(v.number(), v.minValue(0), v.maxValue(100)),
  weight: v.pipe(v.number(), v.minValue(1), v.maxValue(99)),
  discipline: v.pipe(v.number(), v.minValue(0), v.maxValue(100)),
  poopCount: v.pipe(v.number(), v.integer(), v.minValue(0)),
  lastPoopAt: v.pipe(v.number(), v.integer()),
  dead: v.boolean(),
  causeOfDeath: v.nullable(CauseOfDeathSchema),
  tantrum: v.boolean(),
  tantrumStartedAt: v.nullable(v.number()),
  attentionLowSince: v.nullable(v.number()),
  sick: v.boolean(),
  sickSince: v.nullable(v.number()),
  healDoses: v.pipe(v.number(), v.integer(), v.minValue(0)),
  lastHealAt: v.nullable(v.number()),
  lowHealthSince: v.nullable(v.number()),
  vitality: v.optional(v.pipe(v.number(), v.minValue(0), v.maxValue(100)), 100),
});

const CareTierSchema = v.union([
  v.literal('ruim'), v.literal('medio'), v.literal('bom'), v.literal('secreto'),
]);

const LifecycleSchema = v.object({
  stage: StageSchema,
  bornAt: v.pipe(v.number(), v.integer()),
  elderSince: v.nullable(v.number()),
  careTraits: v.optional(v.array(v.string()), []),
  teenEntryProfile: v.optional(v.nullable(CareProfileSchema), null),
  careTier: v.optional(v.nullable(CareTierSchema), null),
  careFinalScore: v.optional(v.nullable(v.number()), null),
});

export const CreatureV6Schema = v.object({
  id: v.string(),
  name: v.string(),
  genome: GenomeSchema,
  state: CreatureStateSchema,
  lifecycle: LifecycleSchema,
  careProfile: v.optional(CareProfileSchema, {}),
  maxHp: v.nullish(v.pipe(v.number(), v.minValue(20), v.maxValue(500))),
  mutations: v.nullish(v.array(v.object({
    id: v.string(),
    pickedAtMs: v.pipe(v.number(), v.integer(), v.minValue(0)),
  }))),
});

const NpcCreatureSchema = v.object({
  id: v.string(),
  name: v.string(),
  genome: GenomeSchema,
  bornAt: v.pipe(v.number(), v.integer()),
  retiredAt: v.pipe(v.number(), v.integer()),
  stage: StageSchema,
  elderSince: v.nullable(v.number()),
  dead: v.boolean(),
  diedAt: v.nullable(v.number()),
  causeOfDeath: v.nullable(CauseOfDeathSchema),
  parentIds: v.tuple([v.nullable(v.string()), v.nullable(v.string())]),
  homeBiome: v.nullish(v.union([
    v.literal('meadow'), v.literal('forest'), v.literal('cave'), v.literal('coast'),
  ])),
  careFinalScore: v.optional(v.nullable(v.number()), null),
});

const LineageNodeSchema = v.object({
  creatureId: v.string(),
  name: v.string(),
  bornAt: v.pipe(v.number(), v.integer()),
  diedAt: v.nullable(v.number()),
  cause: DeathCauseSchema,
  parentIds: v.tuple([v.nullable(v.string()), v.nullable(v.string())]),
  childrenIds: v.array(v.string()),
  genomeSnapshot: GenomeSchema,
  traitsSnapshot: v.array(EvolutionTraitSchema),
});

const LineageGraphSchema = v.object({
  nodes: v.record(v.string(), LineageNodeSchema),
  rootId: v.nullable(v.string()),
});

const BiomeIdSchema = v.union([
  v.literal('meadow'), v.literal('forest'), v.literal('cave'), v.literal('coast'),
]);

const ItemTypeSchema = v.union([
  v.literal('berry'), v.literal('shell'), v.literal('crystal'), v.literal('flower'),
]);

const RegionItemSchema = v.object({
  id: v.string(),
  type: ItemTypeSchema,
  x: v.number(),
  y: v.number(),
  collected: v.boolean(),
});

const InventorySchema = v.object({
  berry: v.pipe(v.number(), v.integer(), v.minValue(0)),
  shell: v.pipe(v.number(), v.integer(), v.minValue(0)),
  crystal: v.pipe(v.number(), v.integer(), v.minValue(0)),
  flower: v.pipe(v.number(), v.integer(), v.minValue(0)),
});

const WildDiscoverySchema = v.object({
  biome: BiomeIdSchema,
  encounters: v.pipe(v.number(), v.integer(), v.minValue(0)),
  dominantSeen: v.boolean(),
  bodies: v.array(BodyGeneSchema),
  ears: v.array(EarGeneSchema),
  horns: v.array(HornGeneSchema),
  tails: v.array(TailGeneSchema),
  markings: v.array(MarkingGeneSchema),
  types: v.array(AlchemyTypeSchema),
});

const WorldStateSchema = v.object({
  position: v.optional(v.object({ x: v.number(), y: v.number() })),
  regionsDiscovered: v.array(BiomeIdSchema),
  inventory: InventorySchema,
  dayCount: v.pipe(v.number(), v.integer(), v.minValue(0)),
  regionItems: v.object({
    meadow: v.array(RegionItemSchema),
    forest: v.array(RegionItemSchema),
    cave: v.array(RegionItemSchema),
    coast: v.array(RegionItemSchema),
  }),
  lastBiome: v.nullable(BiomeIdSchema),
  events: v.nullish(v.array(v.object({
    id: v.string(),
    kind: v.union([v.literal('tree-planted'), v.literal('memorial'), v.literal('species-discovered')]),
    biome: BiomeIdSchema,
    occurredAtMs: v.pipe(v.number(), v.integer()),
    generation: v.pipe(v.number(), v.integer(), v.minValue(0)),
    payload: v.record(v.string(), v.unknown()),
  }))),
  trees: v.nullish(v.array(v.object({
    id: v.string(),
    biome: BiomeIdSchema,
    x: v.number(),
    y: v.number(),
    plantedAtMs: v.pipe(v.number(), v.integer()),
    plantedByNpcId: v.string(),
    stage: v.union([v.literal('sapling'), v.literal('small'), v.literal('adult'), v.literal('ancient')]),
    ageMs: v.pipe(v.number(), v.minValue(0)),
  }))),
  memorials: v.nullish(v.array(v.object({
    id: v.string(),
    npcId: v.string(),
    npcName: v.string(),
    cause: v.union([v.literal('old-age'), v.literal('expedition'), v.literal('neglect')]),
    diedAtMs: v.pipe(v.number(), v.integer()),
    biome: BiomeIdSchema,
    x: v.number(),
    y: v.number(),
  }))),
  enemyKills: v.nullish(v.record(v.string(), v.pipe(v.number(), v.integer(), v.minValue(0)))),
  wildBestiary: v.nullish(v.record(v.string(), WildDiscoverySchema), {}),
  afkDispatches: v.nullish(v.array(v.object({
    id: v.string(),
    npcId: v.string(),
    biome: BiomeIdSchema,
    startedAtMs: v.pipe(v.number(), v.integer()),
    durationMs: v.pipe(v.number(), v.minValue(0)),
    status: v.union([v.literal('active'), v.literal('claimable'), v.literal('claimed')]),
    rewards: v.nullish(v.object({
      items: v.record(v.string(), v.pipe(v.number(), v.integer(), v.minValue(0))),
      xp: v.pipe(v.number(), v.minValue(0)),
    })),
  }))),
  generation: v.nullish(v.pipe(v.number(), v.integer(), v.minValue(0))),
});

const ExpeditionRunSchema = v.object({
  id: v.string(),
  biome: BiomeIdSchema,
  startedAtMs: v.pipe(v.number(), v.integer()),
  endedAtMs: v.nullable(v.number()),
  outcome: v.union([
    v.literal('in-progress'), v.literal('extracted'),
    v.literal('fainted'), v.literal('died'),
  ]),
  enemiesKilled: v.pipe(v.number(), v.integer(), v.minValue(0)),
  xpGained: v.pipe(v.number(), v.minValue(0)),
  pickedMutations: v.array(v.object({
    id: v.string(),
    pickedAtMs: v.pipe(v.number(), v.integer()),
  })),
});

const CombatStateSchema = v.object({
  activeRun: v.nullable(ExpeditionRunSchema),
  totalRuns: v.pipe(v.number(), v.integer(), v.minValue(0)),
});

export const SaveV6Schema = v.object({
  version: v.literal(6),
  schemaMinor: v.pipe(v.number(), v.integer(), v.minValue(0)),
  meta: v.object({
    createdAt: v.pipe(v.number(), v.integer(), v.minValue(0)),
    savedAt: v.pipe(v.number(), v.integer(), v.minValue(0)),
    fastMode: v.boolean(),
    locale: v.union([v.literal('pt-BR'), v.literal('en')]),
  }),
  creature: v.nullish(CreatureV6Schema),
  npcs: v.nullish(v.array(NpcCreatureSchema)),
  lineage: v.nullish(LineageGraphSchema),
  discoveredTypes: v.nullish(v.array(v.string())),
  world: v.nullish(WorldStateSchema),
  // Slots reservados (futuras units; aceitam unknown para compat)
  combat: v.nullish(CombatStateSchema),
  audio: v.nullish(v.unknown()),
  cloud: v.nullish(v.unknown()),
});

export type SaveV6 = v.InferOutput<typeof SaveV6Schema>;
export type CreatureV6 = v.InferOutput<typeof CreatureV6Schema>;
export type NpcCreatureV6 = v.InferOutput<typeof NpcCreatureSchema>;
export type LineageGraphV6 = v.InferOutput<typeof LineageGraphSchema>;

/** Minor 7 migrates every persisted world-space coordinate to the 2x seamless canvas. */
export const CURRENT_SCHEMA_MINOR = 7;

export function emptySaveV6(now: number, locale: 'pt-BR' | 'en' = 'pt-BR'): SaveV6 {
  return {
    version: 6,
    schemaMinor: CURRENT_SCHEMA_MINOR,
    meta: { createdAt: now, savedAt: now, fastMode: false, locale },
  };
}
